import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/site";
import { playChime } from "@/lib/chime";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import LeadDrawer from "@/pages/admin/LeadDrawer";
import DashboardTab from "@/pages/admin/DashboardTab";
import LeadsTab from "@/pages/admin/LeadsTab";
import CalendarTab from "@/pages/admin/CalendarTab";
import CampaignLinksTab from "@/pages/admin/CampaignLinksTab";
import ReportsTab from "@/pages/admin/ReportsTab";
import {
  OFFER_LABELS, statusMeta, priorityMeta,
  isSnoozed, isStale, isHighValue,
  collectSources, APPOINTMENT_TYPES, FOLLOWUP_TYPES, todayISO,
} from "@/pages/admin/leadHelpers";
import {
  LogOut, RefreshCw, Send, Bell, BellOff, LayoutDashboard, Users as UsersIcon,
  CalendarClock, Link2, BarChart3,
} from "lucide-react";

const ADMIN_TABS = [
  { v: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { v: "leads", label: "Leads", icon: UsersIcon },
  { v: "calendar", label: "Calendar", icon: CalendarClock },
  { v: "reports", label: "Reports", icon: BarChart3 },
  { v: "campaign", label: "Campaign Links", icon: Link2 },
];

function hasActivityDueToday(lead, group) {
  const tdy = todayISO();
  return (lead.scheduled_activities || []).some((a) => {
    if (a.status !== "pending" || a.date !== tdy) return false;
    return group === "appointment" ? APPOINTMENT_TYPES.has(a.type) : FOLLOWUP_TYPES.has(a.type);
  });
}
function hasActivityOverdue(lead, group) {
  return (lead.scheduled_activities || []).some((a) => {
    if (a.status !== "pending") return false;
    const inGroup = group === "appointment" ? APPOINTMENT_TYPES.has(a.type) : FOLLOWUP_TYPES.has(a.type);
    if (!inGroup) return false;
    const t = (a.time && /^\d{1,2}:\d{2}$/.test(a.time)) ? a.time : "23:59";
    return new Date(`${a.date}T${t}:00`).getTime() < Date.now();
  });
}
function hasUpcomingAppointment(lead) {
  const tdy = todayISO();
  return (lead.scheduled_activities || []).some((a) => {
    if (a.status !== "pending" || !APPOINTMENT_TYPES.has(a.type)) return false;
    return a.date && a.date >= tdy;
  });
}

/* Onboarding completeness — mirrors DashboardTab.jsx so dashboard
 * "Onboarding In Progress / Submitted" cards filter the Leads-tab list
 * the same way the dashboard counts them. */
const ONBOARDING_REQUIRED_KEYS = ["personal_id", "ssn", "business_tax_id", "voided_check", "personal_phone", "business_phone", "email"];
const ONBOARDING_ALL_KEYS = [...ONBOARDING_REQUIRED_KEYS, "ebt_license"];
function onboardingStateOf(lead) {
  const info = lead?.onboarding_info || {};
  const requiredResolved = ONBOARDING_REQUIRED_KEYS.every((k) => {
    const s = info[k]?.status;
    return s === "received" || s === "not_applicable";
  });
  const anyTouched = ONBOARDING_ALL_KEYS.some((k) => info[k] && info[k].status && info[k].status !== "needed");
  if (requiredResolved && anyTouched) return "submitted";
  if (anyTouched) return "in_progress";
  return "none";
}

function applyQuickFilter(leads, key) {
  if (!key || key === "all") return leads;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  return leads.filter((l) => {
    const age = l.created_at ? now - new Date(l.created_at).getTime() : 0;
    switch (key) {
      case "hot_today":
        return l.priority === "hot" && age <= 7 * day && l.status !== "closed_won" && l.status !== "closed_lost";
      case "new": return l.status === "new";
      case "new_unworked": return l.status === "new" && !l.last_touched_at;
      case "follow_up": return l.status === "follow_up";
      case "info_needed":
      case "statement_needed":
        // Either filter key matches BOTH legacy + new lead status values so
        // pre-existing "Statement Needed" leads still show up.
        return l.status === "info_needed" || l.status === "statement_needed";
      case "followups_due_today": return hasActivityDueToday(l, "follow_up");
      case "followups_overdue": return hasActivityOverdue(l, "follow_up");
      case "appointments_today": return hasActivityDueToday(l, "appointment");
      case "appointments_upcoming": return hasUpcomingAppointment(l);
      case "stale": return l.status === "new" && age >= day && !isSnoozed(l);
      case "snoozed": return isSnoozed(l);
      case "high_value": return isHighValue(l);
      case "duplicates": return (l.possible_duplicate_of || []).length > 0;
      case "has_statement": return !!l.statement_path;
      case "no_statement":
        return (l.offer_type === "savings-analysis" || l.offer_type === "get-started") && !l.statement_path;
      case "onboarding_in_progress": return onboardingStateOf(l) === "in_progress";
      case "onboarding_submitted": return onboardingStateOf(l) === "submitted";
      case "missing_onboarding": {
        // Active leads still needing at least one required onboarding item.
        const info = l.onboarding_info || {};
        return ONBOARDING_REQUIRED_KEYS.some((k) => {
          const s = info[k]?.status;
          return s !== "received" && s !== "not_applicable";
        });
      }
      default: return true;
    }
  });
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Active tab persists via URL hash so refresh / Back to Leads navigation works.
  // We READ the hash only when location changes (router-tracked) — never via raw
  // window.location.hash + replaceState (that combo desyncs after navigating to
  // /admin/leads/:id and back, causing tab-click "freeze").
  const tab = useMemo(() => {
    const h = (location.hash || "").replace("#", "");
    return ADMIN_TABS.find((t) => t.v === h)?.v || "dashboard";
  }, [location.hash]);
  const changeTab = (v) => {
    // navigate updates router state AND the URL together so re-renders see
    // the new hash; no more dual-source-of-truth issue.
    navigate({ pathname: "/admin", hash: `#${v}` }, { replace: true });
  };

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, archived: 0, trash: 0 });
  const [busy, setBusy] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [quickFilter, setQuickFilter] = useState("all");
  const [activeMonth, setActiveMonth] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [view, setView] = useState("active"); // active | archived | trash

  const [soundOn, setSoundOn] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("admin_chime") !== "off";
  });
  const lastTotalRef = useRef(null);
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { window.localStorage.setItem("admin_chime", next ? "on" : "off"); }
      catch (err) { console.warn("[admin] could not persist chime preference:", err); }
      return next;
    });
  };

  // filters
  const [q, setQ] = useState("");
  const [offer, setOffer] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // detail drawer (legacy — Leads-tab View opens full lead page now;
  // we keep the drawer state for backward-compatible flows that still call setOpenLead).
  const [openLead, setOpenLead] = useState(null);

  // Test leads — excluded by default at backend level. When admin flips
  // "Show test leads" (LeadsTab toggle), we pass include_test=true to the
  // server and re-fetch. The dashboard always uses the non-test slice.
  const [showTestLeads, setShowTestLeads] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { view };
      if (q) params.q = q;
      if (offer !== "all") params.offer_type = offer;
      if (status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (showTestLeads) params.include_test = "true";
      const [l, s] = await Promise.all([
        axios.get(`${API_BASE}/admin/leads`, { params, withCredentials: true }),
        axios.get(`${API_BASE}/admin/stats`, { withCredentials: true }),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) navigate("/admin/login");
    } finally {
      setBusy(false);
    }
  }, [q, offer, status, dateFrom, dateTo, view, showTestLeads, navigate]);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  // New-lead chime polling
  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API_BASE}/admin/stats`, { withCredentials: true });
        const total = r.data?.total;
        if (!cancelled && lastTotalRef.current !== null && total > lastTotalRef.current && soundOnRef.current) {
          playChime();
        }
        lastTotalRef.current = total;
      } catch (err) { console.warn("[admin] stats poll failed:", err); }
    };
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  /* Source/month-filtered list — feeds the Leads tab visible list and chip
   * counts. Test leads excluded at the backend (?include_test=false default).
   * When showTestLeads flips on, backend returns all leads; the dashboard
   * STILL filters them out client-side so dashboard cards/popups/chart never
   * inflate, while the Leads-tab list shows everything. */
  const nonTestLeads = useMemo(() => leads.filter((l) => !l.is_test_lead), [leads]);
  const leadsForLeadsTab = useMemo(
    () => (showTestLeads ? leads : nonTestLeads),
    [leads, nonTestLeads, showTestLeads],
  );
  const sourceFiltered = useMemo(() => {
    let out = leadsForLeadsTab;
    if (sourceFilter !== "all") {
      out = out.filter((l) => (l.source_label || "Direct / Unknown") === sourceFilter);
    }
    if (activeMonth) {
      out = out.filter((l) => (l.created_at || "").slice(0, 7) === activeMonth);
    }
    return out;
  }, [leadsForLeadsTab, sourceFilter, activeMonth]);
  const visibleLeads = useMemo(() => applyQuickFilter(sourceFiltered, quickFilter), [sourceFiltered, quickFilter]);
  const chipCount = (key) => (key === "all" ? sourceFiltered.length : applyQuickFilter(sourceFiltered, key).length);
  const sourceOptions = useMemo(() => collectSources(leads), [leads]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }
  if (!user || !user.is_admin) return <Navigate to="/admin/login" replace />;

  /* ─── Mutations (shared across tabs) ─────────────────────────────────────── */
  const updateLead = async (id, patch) => {
    await axios.patch(`${API_BASE}/admin/leads/${id}`, patch, { withCredentials: true });
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    if (openLead?.id === id) setOpenLead((cur) => ({ ...cur, ...patch }));
  };
  const patchLeadLocal = (id, patch) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    if (openLead?.id === id) setOpenLead((cur) => ({ ...cur, ...patch }));
  };
  const downloadStatement = (id) => {
    window.open(`${API_BASE}/admin/leads/${id}/statement`, "_blank");
  };
  const touchLead = async (id, channel) => {
    try {
      const url = channel ? `${API_BASE}/admin/leads/${id}/touch?channel=${channel}` : `${API_BASE}/admin/leads/${id}/touch`;
      const r = await axios.post(url, {}, { withCredentials: true });
      const ts = r.data?.last_touched_at;
      if (ts) patchLeadLocal(id, { last_touched_at: ts });
    } catch (err) { console.warn("[touch] failed:", err); }
  };
  const snoozeLead = async (id) => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads/${id}/snooze`, {}, { withCredentials: true });
      patchLeadLocal(id, {
        snooze_until: r.data?.snooze_until,
        snoozed_by: r.data?.snoozed_by,
        snoozed_at: r.data?.snoozed_at,
        notes: r.data?.notes,
        last_touched_at: r.data?.last_touched_at,
      });
      toast.success("Snoozed for 24 hours");
    } catch {
      toast.error("Unable to snooze lead. Please try again.");
    }
  };
  const unsnoozeLead = async (id) => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads/${id}/unsnooze`, {}, { withCredentials: true });
      patchLeadLocal(id, {
        snooze_until: null, snoozed_by: null, snoozed_at: null,
        notes: r.data?.notes, last_touched_at: r.data?.last_touched_at,
      });
      toast.success("Snooze cancelled");
    } catch {
      toast.error("Unable to cancel snooze. Please try again.");
    }
  };
  const markContacted = async (lead) => {
    const patch = lead.status === "new" ? { status: "contacted" } : {};
    if (Object.keys(patch).length) await updateLead(lead.id, patch);
    await touchLead(lead.id, "contacted");
    toast.success("Marked as contacted");
  };

  const moveLead = async (id, action /* archive | unarchive | trash | restore */) => {
    const labels = { archive: "Archived", unarchive: "Unarchived", trash: "Moved to Trash", restore: "Restored from Trash" };
    try {
      await axios.post(`${API_BASE}/admin/leads/${id}/${action}`, {}, { withCredentials: true });
      toast.success(labels[action] || "Updated");
      if (openLead?.id === id) setOpenLead(null);
      await load();
    } catch {
      toast.error("Action failed");
    }
  };

  const permanentDelete = async (lead) => {
    const phrase = window.prompt(
      `Permanently delete "${lead.business_name || lead.contact_name || lead.id}"?\n\n` +
      `This cannot be undone. Type DELETE to confirm.`
    );
    if (phrase !== "DELETE") {
      toast.info("Permanent delete cancelled");
      return;
    }
    try {
      await axios.delete(`${API_BASE}/admin/leads/${lead.id}?confirm=DELETE`, { withCredentials: true });
      toast.success("Lead permanently deleted");
      if (openLead?.id === lead.id) setOpenLead(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleTestLead = async (id, nextValue) => {
    try {
      await axios.post(
        `${API_BASE}/admin/leads/${id}/test-flag`,
        { is_test_lead: nextValue },
        { withCredentials: true },
      );
      patchLeadLocal(id, { is_test_lead: nextValue });
      toast.success(nextValue ? "Marked as test lead" : "Test lead flag cleared");
      // Re-fetch leads so the dashboard cards/chart immediately reflect the
      // flip — without this, the local patch only updates the lead row but
      // freshly-toggled test leads can linger in dashboard counts if the
      // user navigates away/back before a natural reload.
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update test flag");
    }
  };

  const createManualLead = async (payload) => {
    try {
      const r = await axios.post(`${API_BASE}/admin/leads`, payload, { withCredentials: true });
      toast.success("Lead added");
      await load();
      return r.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add lead");
      throw err;
    }
  };

  /* ─── Bulk actions ──────────────────────────────────────────────────────── */
  const runBulkAction = async (ids, action) => {
    const labels = { archive: "archived", unarchive: "unarchived", trash: "moved to Trash", restore: "restored" };
    let ok = 0; let fail = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await axios.post(`${API_BASE}/admin/leads/${id}/${action}`, {}, { withCredentials: true });
        ok += 1;
      } catch { fail += 1; }
    }));
    if (ok) toast.success(`${ok} lead${ok === 1 ? "" : "s"} ${labels[action] || action}`);
    if (fail) toast.error(`${fail} lead${fail === 1 ? "" : "s"} failed`);
    await load();
  };

  const runBulkPermanentDelete = async (ids) => {
    const phrase = window.prompt(
      `Permanently delete ${ids.length} lead${ids.length === 1 ? "" : "s"}?\n\n` +
      `This cannot be undone. Type DELETE to confirm.`
    );
    if (phrase !== "DELETE") {
      toast.info("Bulk delete cancelled");
      return;
    }
    let ok = 0; let fail = 0;
    await Promise.all(ids.map(async (id) => {
      try {
        await axios.delete(`${API_BASE}/admin/leads/${id}?confirm=DELETE`, { withCredentials: true });
        ok += 1;
      } catch { fail += 1; }
    }));
    if (ok) toast.success(`${ok} lead${ok === 1 ? "" : "s"} permanently deleted`);
    if (fail) toast.error(`${fail} lead${fail === 1 ? "" : "s"} failed`);
    await load();
  };

  /* CSV export of the currently-visible (quick-filter applied) leads. */
  const exportCsv = () => {
    const rows = visibleLeads;
    const cols = [
      ["Date submitted", "created_at"],
      ["Business name", "business_name"],
      ["Contact name", "contact_name"],
      ["Phone", "phone"],
      ["Email", "email"],
      ["Lead type", (l) => OFFER_LABELS[l.offer_type] || l.offer_type || ""],
      ["Source", (l) => l.source_label || "Direct / Unknown"],
      ["Source page", "source_page"],
      ["Monthly processing volume", "monthly_volume"],
      ["Monthly volume amount", "monthly_volume_amount"],
      ["Est. annual value", "est_annual_value"],
      ["Current processor", "current_processor"],
      ["Current POS system", "current_pos"],
      ["Cash discount interest", "cash_discount_interest"],
      ["Free POS interest", "free_pos_interest"],
      ["Status", (l) => statusMeta(l.status).label],
      ["Priority", (l) => priorityMeta(l.priority).label],
      ["Possible duplicate count", (l) => (l.possible_duplicate_of || []).length],
      ["Notes", "notes"],
      ["Statement link", (l) => l.statement_path ? `${API_BASE}/admin/leads/${l.id}/statement` : ""],
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/\r?\n/g, " ").replace(/"/g, '""');
      return /[",]/.test(s) ? `"${s}"` : s;
    };
    const lines = [cols.map(([h]) => esc(h)).join(",")];
    for (const lead of rows) {
      lines.push(cols.map(([, key]) => esc(typeof key === "function" ? key(lead) : lead[key])).join(","));
    }
    const csv = lines.join("\r\n");
    const today = new Date().toISOString().slice(0, 10);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `swipetronics-leads-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      await axios.post(`${API_BASE}/admin/digest/run`, {}, { withCredentials: true });
      toast.success("Lead digest sent to leads@swipetronics.com");
    } catch {
      toast.error("Unable to send digest. Please try again.");
    } finally {
      setSendingDigest(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard" data-prerender-ready="true">
      <Seo {...getSeoForPath("/admin")} />
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[80px] py-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex items-center gap-4 min-w-0">
            <Logo
              variant="dark"
              to="/admin"
              className="h-11 lg:h-12 w-auto select-none"
              linkClassName="shrink-0"
            />
            <div className="hidden sm:block h-9 w-px bg-slate-200" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--swipe-accent)] font-bold whitespace-nowrap">
                Admin Portal
              </span>
              <span className="text-[12px] text-slate-500 whitespace-nowrap">
                Leads · Follow-ups · Campaigns
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
            <button
              onClick={load} disabled={busy}
              data-testid="admin-refresh-top"
              className="hidden sm:inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium whitespace-nowrap transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button" onClick={toggleSound} data-testid="admin-toggle-sound"
              title={soundOn ? "New-lead sound is on" : "New-lead sound is off"}
              aria-label={soundOn ? "Mute new-lead sound" : "Enable new-lead sound"}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors"
            >
              {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4 opacity-60" />}
            </button>
            <button
              type="button" onClick={sendDigest} disabled={sendingDigest}
              data-testid="admin-send-digest"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 sm:px-4 rounded-lg bg-[var(--swipe-accent)] hover:bg-[#005ed1] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold whitespace-nowrap transition-colors"
            >
              {sendingDigest ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /><span className="hidden sm:inline">Sending…</span></>
              ) : (
                <><Send className="w-4 h-4" /><span className="hidden sm:inline">Send Digest</span></>
              )}
            </button>
            <div className="hidden lg:flex items-center gap-2.5 pl-4 ml-1 border-l border-slate-200 min-w-0">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-1 ring-slate-200 shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--swipe-soft-blue)] text-[var(--swipe-accent)] inline-flex items-center justify-center text-[12px] font-bold shrink-0">
                  {(user.email || "?")[0].toUpperCase()}
                </div>
              )}
              <span className="text-sm text-slate-700 font-medium truncate max-w-[200px]" title={user.email} data-testid="admin-email">
                {user.email}
              </span>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium whitespace-nowrap transition-colors"
            >
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        {/* TABS NAV */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-tabs">
          <div className="flex items-center gap-1 overflow-x-auto -mb-px">
            {ADMIN_TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => changeTab(t.v)}
                  data-testid={`tab-${t.v}`}
                  className={`inline-flex items-center gap-2 px-4 py-3 text-[14px] font-extrabold border-b-2 whitespace-nowrap transition-colors ${
                    active
                      ? "text-[var(--swipe-accent)] border-[var(--swipe-accent)]"
                      : "text-slate-500 hover:text-slate-700 border-transparent"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {tab === "dashboard" ? (
          <DashboardTab
            leads={nonTestLeads}
            sourceFilter={sourceFilter}
            activeMonth={activeMonth}
            onPickMonth={(m) => setActiveMonth((cur) => (cur === m ? null : m))}
            onRefresh={load}
            onViewAllInLeads={(intent) => {
              // Reset all filter state, then apply the intent's filter shape,
              // then navigate to the Leads tab. The LeadsTab effect that
              // watches view/q/offer/.../quickFilter/activeMonth will reset
              // pagination to page 1 automatically.
              setQ(""); setOffer("all"); setStatus("all"); setSourceFilter("all");
              setActiveMonth(null);
              setView("active");
              setDateFrom(intent?.dateFrom || "");
              setDateTo(intent?.dateTo || "");
              setQuickFilter(intent?.quickFilter || "all");
              navigate({ pathname: "/admin", hash: "#leads" }, { replace: true });
            }}
            onPickSource={(src) => {
              // Source Performance card click — reset filters, apply source
              // filter, then switch to Leads tab.
              setQ(""); setOffer("all"); setStatus("all");
              setDateFrom(""); setDateTo("");
              setActiveMonth(null);
              setView("active");
              setQuickFilter("all");
              setSourceFilter(src || "all");
              navigate({ pathname: "/admin", hash: "#leads" }, { replace: true });
            }}
          />
        ) : null}

        {tab === "leads" ? (
          <LeadsTab
            leads={visibleLeads}
            stats={stats}
            view={view}
            busy={busy}
            q={q} offer={offer} status={status} sourceFilter={sourceFilter}
            dateFrom={dateFrom} dateTo={dateTo} quickFilter={quickFilter}
            sourceOptions={sourceOptions}
            activeMonth={activeMonth}
            showTestLeads={showTestLeads}
            onToggleShowTestLeads={() => setShowTestLeads((v) => !v)}
            onCreateManualLead={createManualLead}
            onSetView={setView}
            onSetQ={setQ} onSetOffer={setOffer} onSetStatus={setStatus}
            onSetSourceFilter={setSourceFilter} onSetDateFrom={setDateFrom} onSetDateTo={setDateTo}
            onSetQuickFilter={setQuickFilter}
            onClearActiveMonth={() => setActiveMonth(null)}
            onClearAllFilters={() => {
              setQ(""); setOffer("all"); setStatus("all"); setSourceFilter("all");
              setDateFrom(""); setDateTo(""); setActiveMonth(null);
            }}
            onRefresh={load}
            onExportCsv={exportCsv}
            onUpdateLead={updateLead}
            onSnoozeLead={snoozeLead}
            onUnsnoozeLead={unsnoozeLead}
            onBulkArchive={(ids) => runBulkAction(ids, "archive")}
            onBulkUnarchive={(ids) => runBulkAction(ids, "unarchive")}
            onBulkTrash={(ids) => runBulkAction(ids, "trash")}
            onBulkRestore={(ids) => runBulkAction(ids, "restore")}
            onBulkPermanentDelete={runBulkPermanentDelete}
            chipCount={chipCount}
          />
        ) : null}

        {tab === "calendar" ? (
          <CalendarTab leads={leads} onLeadPatched={patchLeadLocal} />
        ) : null}

        {tab === "campaign" ? (
          <CampaignLinksTab />
        ) : null}

        {tab === "reports" ? (
          <ReportsTab
            leads={nonTestLeads}
            onPickSource={(src) => {
              setQ(""); setOffer("all"); setStatus("all");
              setDateFrom(""); setDateTo("");
              setActiveMonth(null);
              setView("active");
              setQuickFilter("all");
              setSourceFilter(src || "all");
              navigate({ pathname: "/admin", hash: "#leads" }, { replace: true });
            }}
          />
        ) : null}
      </main>

      {/* Legacy drawer kept for any in-page Open Lead calls. The Leads-tab View
       * action navigates to /admin/leads/:id (a full page) instead. */}
      {openLead ? (
        <LeadDrawer
          lead={openLead}
          allLeads={leads}
          view={view}
          onClose={() => setOpenLead(null)}
          onUpdate={updateLead}
          onDownload={() => downloadStatement(openLead.id)}
          onTouch={(channel) => touchLead(openLead.id, channel)}
          onMarkContacted={() => markContacted(openLead)}
          onSnooze={() => snoozeLead(openLead.id)}
          onUnsnooze={() => unsnoozeLead(openLead.id)}
          onOpenLead={(l) => setOpenLead(l)}
          onLeadPatched={patchLeadLocal}
          onArchive={() => moveLead(openLead.id, "archive")}
          onUnarchive={() => moveLead(openLead.id, "unarchive")}
          onTrash={() => moveLead(openLead.id, "trash")}
          onRestore={() => moveLead(openLead.id, "restore")}
          onPermanentDelete={() => permanentDelete(openLead)}
          onToggleTestLead={(next) => toggleTestLead(openLead.id, next)}
        />
      ) : null}
    </div>
  );
}
